import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import type { PastDueInvoiceRow, PastDueInvoicesMeta } from './billing-dashboard.types';
import { formatBillingCurrency, formatBillingDate, formatBillingDateTime } from './billing-dashboard-format';

type SortKey = 'invoiceNumber' | 'clientName' | 'dueDate' | 'daysOverdue' | 'amount';

type Props = {
  pastDue: PastDueInvoiceRow[];
  meta: PastDueInvoicesMeta;
  isLoading?: boolean;
};

const PAGE_SIZE = 10;

function sortRows(rows: PastDueInvoiceRow[], key: SortKey, dir: 'asc' | 'desc'): PastDueInvoiceRow[] {
  const mult = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'daysOverdue':
      case 'amount':
        cmp = a[key] - b[key];
        break;
      case 'dueDate':
        cmp = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        break;
      default:
        cmp = String(a[key]).localeCompare(String(b[key]));
    }
    return cmp * mult;
  });
}

const BillingPastDueInvoicesSection: React.FC<Props> = ({ pastDue, meta, isLoading = false }) => {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>('daysOverdue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);

  const sorted = useMemo(
    () => sortRows(pastDue, sortKey, sortDir),
    [pastDue, sortKey, sortDir]
  );

  const totalCount = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    setPage(0);
  }, [pastDue, sortKey, sortDir]);

  useEffect(() => {
    if (page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = page * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [sorted, page]);

  const rangeStart = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, totalCount);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'daysOverdue' ? 'desc' : 'asc');
    }
  };

  const SortIcon: React.FC<{ column: SortKey }> = ({ column }) => {
    if (sortKey !== column) {
      return (
        <ArrowUpDown
          className="ml-1 inline size-3.5 opacity-40"
          aria-hidden
        />
      );
    }
    return sortDir === 'asc' ? (
      <ArrowUp
        className="ml-1 inline size-3.5"
        aria-hidden
      />
    ) : (
      <ArrowDown
        className="ml-1 inline size-3.5"
        aria-hidden
      />
    );
  };

  const openInvoice = (internalInvoiceId: string) => {
    navigate(`/billing/invoices?invoiceId=${encodeURIComponent(internalInvoiceId)}`);
  };

  const lastUpdatedLabel = meta.refreshedAt
    ? `Last updated ${formatBillingDateTime(meta.refreshedAt)}`
    : null;

  return (
    <section
      className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5"
      aria-labelledby="billing-past-due-heading"
    >
      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2
            id="billing-past-due-heading"
            className="text-base font-semibold text-gray-900"
          >
            Outstanding invoices past due
          </h2>
          <p className="text-sm text-gray-600">
            Invoices with a due date before today that remain unpaid, sourced from QuickBooks.
          </p>
        </div>
        {lastUpdatedLabel ? (
          <p className="text-xs text-gray-500 sm:text-right">
            {lastUpdatedLabel}
          </p>
        ) : null}
      </div>

      {meta.error ? (
        <p
          className="mb-3 text-sm text-amber-800"
          role="status"
        >
          {meta.stale
            ? 'QuickBooks could not be reached. Showing the last cached outstanding invoices.'
            : 'QuickBooks could not be reached. Outstanding invoices are temporarily unavailable.'}
        </p>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-gray-600">
          Loading outstanding invoices…
        </p>
      ) : pastDue.length === 0 ? (
        <p className="text-sm text-gray-600">
          No outstanding invoices.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[1%] whitespace-nowrap">
                  <button
                    type="button"
                    className="inline-flex items-center font-medium hover:text-gray-900"
                    onClick={() => toggleSort('invoiceNumber')}
                  >
                    Invoice #
                    <SortIcon column="invoiceNumber" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center font-medium hover:text-gray-900"
                    onClick={() => toggleSort('clientName')}
                  >
                    Client / company
                    <SortIcon column="clientName" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center font-medium hover:text-gray-900"
                    onClick={() => toggleSort('dueDate')}
                  >
                    Due date
                    <SortIcon column="dueDate" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center font-medium hover:text-gray-900"
                    onClick={() => toggleSort('daysOverdue')}
                  >
                    Days overdue
                    <SortIcon column="daysOverdue" />
                  </button>
                </TableHead>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center font-medium hover:text-gray-900"
                    onClick={() => toggleSort('amount')}
                  >
                    Amount
                    <SortIcon column="amount" />
                  </button>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((row) => {
                const severe = row.daysOverdue > 60;
                const isClickable = Boolean(row.internalInvoiceId);
                return (
                  <TableRow
                    key={row.qboInvoiceId}
                    className={[
                      severe ? 'bg-red-50/90 hover:bg-red-50' : undefined,
                      isClickable ? 'cursor-pointer hover:bg-gray-50' : undefined,
                    ].filter(Boolean).join(' ') || undefined}
                    onClick={
                      isClickable && row.internalInvoiceId
                        ? () => openInvoice(row.internalInvoiceId as string)
                        : undefined
                    }
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={
                      isClickable && row.internalInvoiceId
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openInvoice(row.internalInvoiceId as string);
                            }
                          }
                        : undefined
                    }
                  >
                    <TableCell className="whitespace-nowrap font-medium">
                      {row.invoiceNumber}
                    </TableCell>
                    <TableCell>
                      {row.clientName}
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {row.dueDate ? formatBillingDate(row.dueDate) : '—'}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {row.daysOverdue}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {formatBillingCurrency(row.amount)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {pastDue.length > 0 ? (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-gray-600 tabular-nums">
            Showing
            {' '}
            {rangeStart}
            –
            {rangeEnd}
            {' '}
            of
            {' '}
            {totalCount}
          </p>
          {totalPages > 1 ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={page <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <span className="text-xs text-gray-600 tabular-nums">
                Page
                {' '}
                {page + 1}
                {' '}
                of
                {' '}
                {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default BillingPastDueInvoicesSection;
